import { coerceBooleanProperty } from "@angular/cdk/coercion";
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, Input, OnDestroy, Renderer2 } from "@angular/core";
import {
    BoundingSphere,
    Cartesian2,
    Cartesian3,
    EventHelper,
    Intersect,
    JulianDate,
    Occluder,
    PositionProperty,
    SceneTransforms,
} from "cesium";
import { CesiumMapService } from "../../services/cesium-map.service";

@Component({
    selector: "dtm-map-html-cesium-component",
    template: "<ng-content></ng-content>",
    styles: [
        `
            :host {
                position: absolute;
                z-index: 1;
            }
        `,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HtmlCesiumComponent implements AfterViewInit, OnDestroy {
    @Input() public set coordinates(value: Cartesian2 | Cartesian3 | undefined) {
        if (!value) {
            return;
        }
        this.entity.position = value as unknown as PositionProperty;
    }

    @Input({
        transform: coerceBooleanProperty,
    })
    public shouldHideOccluded = true;

    @Input({
        transform: coerceBooleanProperty,
    })
    public shouldHideOutOfView = true;

    private readonly entity;
    private readonly eventHelper = new EventHelper();
    private htmlElement: HTMLHtmlElement | undefined;
    private currentPosition: PositionProperty | undefined;
    private currentPosition2d: Cartesian2 | undefined;

    constructor(
        private renderer: Renderer2,
        private readonly elementRef: ElementRef<HTMLHtmlElement>,
        private readonly cesiumService: CesiumMapService
    ) {
        this.entity = this.cesiumService.getViewer().entities.add({
            position: Cartesian3.fromDegrees(0, 0),
        });
    }

    public ngAfterViewInit(): void {
        this.htmlElement = this.elementRef.nativeElement;

        this.cesiumService.getViewer().clock.onTick.addEventListener(() => {});
        this.eventHelper.add(this.cesiumService.getViewer().clock.onTick, this.handlePositionUpdates, this);
    }

    public ngOnDestroy(): void {
        this.eventHelper.removeAll();
        this.cesiumService.getViewer().entities.remove(this.entity);
    }

    public handlePositionUpdates() {
        if (!this.entity.position) {
            return;
        }

        this.currentPosition = this.entity.position;
        let positionUpdate: Cartesian2 | undefined;
        const positionValue = this.currentPosition.getValue(new JulianDate()) as Cartesian3;

        if (!positionValue) {
            return;
        }

        const isPositionInView = this.isPositionOccluded(positionValue) && this.isPositionInView(positionValue);

        if (isPositionInView) {
            positionUpdate = SceneTransforms.worldToDrawingBufferCoordinates(
                this.cesiumService.getViewer().scene,
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                this.currentPosition.getValue(new JulianDate())!
            ) as Cartesian2;
        } else {
            this.renderer.setStyle(this.htmlElement, "display", "none");

            return;
        }

        if ((this.currentPosition2d && positionUpdate?.equals(this.currentPosition2d)) || !positionUpdate) {
            return;
        }

        if (!positionUpdate) {
            return;
        }

        this.renderer.setStyle(this.htmlElement, "display", "block");
        this.renderer.setStyle(this.htmlElement, "left", `${positionUpdate.x}px`);
        this.renderer.setStyle(this.htmlElement, "top", `${positionUpdate.y}px`);

        this.currentPosition2d = positionUpdate;
    }

    private isPositionOccluded(position: Cartesian3) {
        if (!this.shouldHideOccluded) {
            return true;
        }
        const globeBoundingSphere = new BoundingSphere(Cartesian3.ZERO, this.cesiumService.getViewer().scene.globe.ellipsoid.minimumRadius);

        return new Occluder(globeBoundingSphere, this.cesiumService.getViewer().camera.position).isPointVisible(position);
    }

    private isPositionInView(entityPosition: Cartesian3) {
        if (!this.shouldHideOutOfView) {
            return true;
        }

        const camera = this.cesiumService.getViewer().camera;
        const { position, direction, up } = camera;

        return (
            camera.frustum.computeCullingVolume(position, direction, up).computeVisibility(new BoundingSphere(entityPosition, 0)) !==
            Intersect.OUTSIDE
        );
    }
}
